import { AppLayout } from "@/components/Layout/AppLayout";
import { AuthLayout } from "@/components/Layout/AuthLayout";
import { ProtectedRoute } from "@/components/Layout/ProtectedRoute";
import { PageLoader } from "@/components/ui/PageLoader";
import { useAppSocket } from "@/hooks/useAppSocket";
import { useAuth } from "@/hooks/useAuth";
import { useAppSelector } from "@/store/index";
import { Suspense, lazy } from "react";
import { Navigate, Route, Routes } from "react-router-dom";

// Auth
const AuthPage = lazy(() => import("@/pages/Auth/AuthPage"));
const ResetPasswordPage = lazy(() => import("@/pages/Auth/ResetPasswordPage"));
const VerifyEmailPage = lazy(() => import("@/pages/Auth/VerifyEmailPage"));
const GoogleCallbackPage = lazy(() => import("@/pages/Auth/GoogleCallbackPage"));

// Public
const LandingPage = lazy(() => import("@/pages/Landing/LandingPage"));
const NotFoundPage = lazy(() => import("@/pages/NotFoundPage"));
const LegalPage = lazy(() => import("@/pages/Legal/LegalPage"));

// App
const DashboardPage = lazy(() => import("@/pages/Dashboard/DashboardPage"));
const GeneratePage = lazy(() => import("@/pages/Generate/GeneratePage"));
const HistoryPage = lazy(() => import("@/pages/History/HistoryPage"));
const TemplatesPage = lazy(() => import("@/pages/Templates/TemplatesPage"));
const PricingPage = lazy(() => import("@/pages/Pricing/PricingPage"));
const ProfilePage = lazy(() => import("@/pages/Profile/ProfilePage"));
const AdminPage = lazy(() => import("@/pages/Admin/AdminPage"));
const FavoritesPage = lazy(() => import("@/pages/Favorites/FavoritesPage"));
const VoicePage = lazy(() => import("@/pages/Voice/VoicePage"));

function AuthInitializer() {
  useAuth();
  return null;
}

/** Gère la connexion socket temps-réel (sync crédits + notifications). */
function SocketManager() {
  useAppSocket();
  return null;
}

/** Route racine : Landing si non connecté, Dashboard si connecté */
function RootRoute() {
  const { isAuthenticated, isLoading } = useAppSelector((s) => s.auth);
  if (isLoading) return <PageLoader />;
  if (isAuthenticated) return <Navigate to="/dashboard" replace />;
  return <LandingPage />;
}

export function App() {
  return (
    <>
      <AuthInitializer />
      <SocketManager />
      <Suspense fallback={<PageLoader />}>
        <Routes>
          {/* Landing page publique */}
          <Route path="/" element={<RootRoute />} />

          {/* Pages auth publiques — /login /register /forgot-password partagent AuthPage */}
          <Route element={<AuthLayout />}>
            <Route path="/login" element={<AuthPage />} />
            <Route path="/register" element={<AuthPage />} />
            <Route path="/forgot-password" element={<AuthPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="/verify-email" element={<VerifyEmailPage />} />
          </Route>
          {/* Google OAuth callback — outside AuthLayout so it can navigate freely */}
          <Route path="/auth/callback" element={<GoogleCallbackPage />} />

          {/* Page tarifs — publique (non connecté → /register, connecté → Stripe) */}
          <Route path="/pricing" element={<PricingPage />} />

          {/* Pages légales — publiques */}
          <Route path="/privacy" element={<LegalPage type="privacy" />} />
          <Route path="/terms" element={<LegalPage type="terms" />} />

          {/* Routes protégées */}
          <Route element={<ProtectedRoute />}>
            <Route element={<AppLayout />}>
              <Route path="/dashboard" element={<DashboardPage />} />
              <Route path="/generate" element={<GeneratePage />} />
              <Route path="/history" element={<HistoryPage />} />
              <Route path="/templates" element={<TemplatesPage />} />
              <Route path="/profile" element={<ProfilePage />} />
              <Route path="/favorites" element={<FavoritesPage />} />
              <Route path="/voice" element={<VoicePage />} />
            </Route>
          </Route>

          {/* Admin */}
          <Route element={<ProtectedRoute requiredRole="admin" />}>
            <Route element={<AppLayout />}>
              <Route path="/admin" element={<AdminPage />} />
            </Route>
          </Route>

          {/* Fallback 404 */}
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </Suspense>
    </>
  );
}
